import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from 'constants/apiConfig';
import { observability } from 'utils/observability';
import {
  filesOrFoldersKeys,
  type FileOrFolder,
  type NotebookFolder,
} from 'components/FileManagement/hooks/useGetAllFiles';
import {
  mapResourceSetToEditPayload,
  resourceSetToApiBody,
  keys as resourceSetKeys,
  useResourceSets,
} from 'components/IAP/hooks/resourceSetHooks';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  DEFAULT_ASSET_DOCUMENT_FOLDERS,
  normalizeAssetDocumentFolders,
  type AssetDocumentFolder,
} from './assetDocumentFolders';

const ORG_SCOPE = 'organization' as const;
const ASSETS_ROOT_FOLDER_NAME = 'Assets';

const REQUIRED_FOLDER_NAMES = DEFAULT_ASSET_DOCUMENT_FOLDERS.map(
  (folder) => folder.name
);

/** True when Drawings or Procedures has no org folder yet. */
export function assetFoldersMissingNotefolderIds(
  folders: AssetDocumentFolder[] | null | undefined
): boolean {
  const normalized = normalizeAssetDocumentFolders(folders);
  return REQUIRED_FOLDER_NAMES.some(
    (name) => !normalized.find((folder) => folder.name === name)?.notefolderId
  );
}

function isFolder(item: FileOrFolder): item is NotebookFolder {
  return item.type === 'folder';
}

/**
 * Finds `name` under `parentId` in the org file library, creating it when it
 * does not exist yet. Returns the folder id.
 */
async function findOrCreateOrgFolder(
  orgGuid: string,
  existing: NotebookFolder[],
  name: string,
  parentId: string | null
): Promise<string> {
  const found = existing.find(
    (folder) =>
      folder.folder_name === name && (folder.notefolderId ?? null) === parentId
  );
  if (found) return found.id;
  const { data } = await axios.post<NotebookFolder>(
    apiConfig.apiGateway.foldersBase,
    {
      name,
      workspaceId: orgGuid,
      scopeType: ORG_SCOPE,
      parentId,
    }
  );
  existing.push(data);
  return data.id;
}

/**
 * Makes sure the org file library holds `Assets / <asset name> / Drawings` and
 * `Procedures`, and returns the asset's folders with their `notefolderId`s
 * filled in. Folders that already carry an id are left alone.
 */
export async function ensureAssetOrgFolders({
  orgGuid,
  assetName,
  folders,
}: {
  orgGuid: string;
  assetName: string;
  folders?: AssetDocumentFolder[] | null;
}): Promise<AssetDocumentFolder[]> {
  const normalized = normalizeAssetDocumentFolders(folders);
  if (!assetFoldersMissingNotefolderIds(normalized)) return normalized;

  const { data } = await axios.get<FileOrFolder[]>(
    apiConfig.apiGateway.foldersBase,
    {
      params: { workspaceId: orgGuid, recursive: true, scopeType: ORG_SCOPE },
    }
  );
  const existing = data.filter(isFolder);

  const assetsRootId = await findOrCreateOrgFolder(
    orgGuid,
    existing,
    ASSETS_ROOT_FOLDER_NAME,
    null
  );
  const assetFolderId = await findOrCreateOrgFolder(
    orgGuid,
    existing,
    assetName.trim() || 'Asset',
    assetsRootId
  );

  const result: AssetDocumentFolder[] = [];
  for (const folder of normalized) {
    if (folder.notefolderId || !REQUIRED_FOLDER_NAMES.includes(folder.name)) {
      result.push(folder);
      continue;
    }
    const notefolderId = await findOrCreateOrgFolder(
      orgGuid,
      existing,
      folder.name,
      assetFolderId
    );
    result.push({ ...folder, notefolderId });
  }
  return result;
}

/** Returns a stable function that ensures org folders for a new/existing asset. */
export function useEnsureAssetOrgFolders() {
  const orgGuid = useAppSelector(selectGroupGuid) as string | undefined;
  const queryClient = useQueryClient();

  return useCallback(
    async (args: {
      assetName: string;
      folders?: AssetDocumentFolder[] | null;
    }) => {
      const normalized = normalizeAssetDocumentFolders(args.folders);
      if (!orgGuid) return normalized;
      const next = await ensureAssetOrgFolders({
        orgGuid,
        assetName: args.assetName,
        folders: normalized,
      });
      queryClient.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(orgGuid),
      });
      return next;
    },
    [orgGuid, queryClient]
  );
}

/**
 * HWCG: when an asset is opened and Drawings / Procedures have no org folder,
 * create them and save the ids on the asset. Failures (for example a user
 * without file-library access) are swallowed; the folder list still renders.
 */
export function useEnsureAssetFoldersOnOpen({
  enabled,
  resourceSetId,
  assetName,
  folders,
  onFoldersChange,
}: {
  enabled: boolean;
  resourceSetId?: string;
  assetName?: string;
  folders?: AssetDocumentFolder[] | null;
  onFoldersChange?: (folders: AssetDocumentFolder[]) => void;
}) {
  const hwcg = useHwcgFlag();
  const ensure = useEnsureAssetOrgFolders();
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const { data: resourceSets } = useResourceSets();
  const attempted = useRef<Set<string>>(new Set());
  const onFoldersChangeRef = useRef(onFoldersChange);
  useEffect(() => {
    onFoldersChangeRef.current = onFoldersChange;
  }, [onFoldersChange]);

  const missing = assetFoldersMissingNotefolderIds(folders);
  const currentResourceSet = Array.isArray(resourceSets)
    ? resourceSets.find((set) => set.resource_set_id === resourceSetId)
    : undefined;

  useEffect(() => {
    if (!hwcg || !enabled || !resourceSetId || !missing) return;
    if (!currentResourceSet) return;
    if (attempted.current.has(resourceSetId)) return;
    attempted.current.add(resourceSetId);

    (async () => {
      try {
        const next = await ensure({
          assetName: assetName || currentResourceSet.name,
          folders,
        });
        const payload = {
          ...mapResourceSetToEditPayload(resourceSetId, currentResourceSet),
          documentFolders: next,
        };
        await axios.put(
          `${apiConfig.apiGateway.ics_resource_sets}/${resourceSetId}`,
          {
            ...resourceSetToApiBody(payload as Record<string, unknown>),
            user_guid: userGuid,
            group_guid: groupGuid,
            incident_id: incidentId ?? null,
          }
        );
        queryClient.invalidateQueries({ queryKey: resourceSetKeys.all });
        onFoldersChangeRef.current?.(next);
      } catch (error) {
        observability.captureException(error, {
          context: 'ensureAssetOrgFolders',
        });
      }
    })();
  }, [
    hwcg,
    enabled,
    resourceSetId,
    missing,
    currentResourceSet,
    assetName,
    folders,
    ensure,
    queryClient,
    groupGuid,
    userGuid,
    incidentId,
  ]);
}
